import React, { useContext } from 'react';
import { DateTime } from 'luxon';

import { app, media } from 'context';
import { Animate, Clickable, DatePicker, Layout, Text } from 'ds';
import { actions } from 'store/ServicesRequest';
import { selectServicesRequestHasMovedIn, selectServicesRequestMoveInDate } from 'store/ServicesRequest/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import ServicesRequestBottomSheet from './ServicesRequestBottomSheet';
import StepContainer from './StepContainer';
import StepTitle from './StepTitle';
import { generateServicesRequestStepPath } from './utils';

interface Props {}

const ServicesRequestMove: React.FC<Props> = () => {
  const moveInDate = useAppSelector(selectServicesRequestMoveInDate);
  const dispatch = useAppDispatch();
  const { xs, sm } = useContext(media);
  const isMobile = xs || sm;
  const { isSmallPhone } = useContext(app);
  const hasMovedIn = useAppSelector(selectServicesRequestHasMovedIn);

  const content = (
    <Layout direction="column" gap={16}>
      <Animate duration={300} delay={100}>
        <Clickable
          onClick={() => {
            dispatch(actions.setHasMovedIn(true));
          }}
          fullWidth
        >
          <Layout
            borderRadius={16}
            {...(hasMovedIn === true
              ? { color: 'blue-50', borderColor: 'blue-500', borderWidth: 2, paddingX: 23, paddingY: 19 }
              : { color: 'white', borderColor: 'gray-50', borderWidth: 1, paddingX: 24, paddingY: 20 })}
            height={76}
            gap={8}
            align="center"
          >
            <Text size="headline-xs" align="left">
              Yes
            </Text>
          </Layout>
        </Clickable>
      </Animate>
      <Animate duration={300} delay={200}>
        <Clickable
          onClick={() => {
            if (hasMovedIn === false) return;

            dispatch(actions.setHasMovedIn(false));
            dispatch(actions.setMoveInDate(null));
          }}
          fullWidth
        >
          <Layout
            borderRadius={16}
            {...(hasMovedIn === false
              ? { color: 'blue-50', borderColor: 'blue-500', borderWidth: 2, paddingX: 23, paddingY: 19 }
              : { color: 'white', borderColor: 'gray-50', borderWidth: 1, paddingX: 24, paddingY: 20 })}
            height={hasMovedIn === false ? undefined : 76}
            gap={8}
            align="center"
          >
            <Layout gap={24} direction="column">
              <Text size="headline-xs" align="left">
                No, not yet
              </Text>
              {hasMovedIn === false && (
                <DatePicker
                  onChange={date => {
                    dispatch(actions.setMoveInDate(date ? DateTime.fromJSDate(date).toFormat('yyyy-MM-dd') : null));
                  }}
                  autoFocus={!moveInDate}
                  isBottomSheet={isMobile}
                  selected={moveInDate ? DateTime.fromISO(moveInDate).toJSDate() : null}
                  type="marketing"
                  allowPastDates={false}
                  placeholder="Est. move-in date (optional)"
                  minDate={DateTime.now().toJSDate()}
                />
              )}
            </Layout>
          </Layout>
        </Clickable>
      </Animate>
    </Layout>
  );

  return (
    <StepContainer
      step="move"
      nextHref={generateServicesRequestStepPath({ step: hasMovedIn ? 'contact' : 'congrats' })}
      backHref={generateServicesRequestStepPath({ step: 'size' })}
      ctaDisabled={hasMovedIn === null}
      textLayoutProps={isSmallPhone ? { marginTop: 24 } : undefined}
      stepCompletedEventValue={{ moveInDate, hasMovedIn }}
      stepName="move date"
    >
      <Layout>
        <StepTitle>Have you moved in yet?</StepTitle>
      </Layout>
      {isMobile ? (
        <ServicesRequestBottomSheet>
          <Layout paddingX={24} paddingBottom={24} width="100%">
            {content}
          </Layout>
        </ServicesRequestBottomSheet>
      ) : (
        content
      )}
    </StepContainer>
  );
};

export default ServicesRequestMove;
